import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  useTheme, 
  useMediaQuery, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  Box,
  Container
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionIcon from '@mui/icons-material/Description';
import { motion, useScroll } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { scrollY } = useScroll();
  
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    return scrollY.onChange((latestScrollValue) => {
      setIsScrolled(latestScrollValue > 50);
    });
  }, [scrollY]);

  const handleResumeClick = () => {
    window.open(process.env.PUBLIC_URL + '/cv.pdf', '_blank');
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Experience', path: '/experience' },
    { label: 'Projects', path: '/projects' },
    { label: 'Skills', path: '/skills' },
    { label: 'Contact', path: '/contact' }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const ResumeButton = () => (
    <Button
      onClick={handleResumeClick}
      startIcon={<DescriptionIcon />}
      sx={{
        ml: { xs: 0, md: 2 },
        color: 'var(--primary)',
        border: '1px solid var(--primary)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 8px rgba(255, 255, 255, 0.1)'
        },
        transition: 'all 0.2s ease'
      }}
    >
      Resume
    </Button>
  );

  return (
    <AppBar 
      position="sticky"
      elevation={isScrolled ? 4 : 0}
      sx={{
        top: 0,
        zIndex: 1100,
        background: isScrolled ? 'rgba(10, 20, 21, 0.95)' : 'transparent',
        backdropFilter: 'blur(12px)',
        borderBottom: isScrolled ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
        transition: 'all 0.4s ease',
        py: isScrolled ? 0.5 : 1,
        mb: 2
      }}
    >
      <Container maxWidth="xl" sx={{ mt: 0 }}>
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Typography 
            variant="h6" 
            component={Link} 
            to="/"
            sx={{
              textDecoration: 'none',
              fontFamily: "'Chivo Mono', monospace",
              fontWeight: 700,
              fontSize: { xs: '1.4rem', sm: '1.6rem' },
              background: 'linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.7))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            &lt;Onkar /&gt;
          </Typography>

          {isMobile ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ResumeButton />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={toggleMobileMenu}
                  sx={{ 
                    color: 'var(--primary)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    component={Link}
                    to={item.path}
                    sx={{
                      mx: 1.5,
                      color: '#ffffff',
                      position: 'relative',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: isActive(item.path) ? '100%' : '0%',
                        height: '2px',
                        bottom: 0,
                        left: 0,
                        background: 'var(--primary)',
                        transition: 'all 0.2s ease'
                      },
                      '&:hover::after': {
                        width: '100%'
                      },
                      ...(isActive(item.path) && {
                        color: 'var(--primary)',
                        fontWeight: 700
                      })
                    }}
                  >
                    {item.label}
                  </Button>
                </motion.div>
              ))}
              <ResumeButton />
            </Box>
          )}
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        anchor="right"
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        ModalProps={{
          keepMounted: true
        }}
        PaperProps={{
          sx: {
            width: 260,
            bgcolor: 'rgba(10, 20, 21, 0.98)',
            backdropFilter: 'blur(10px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.05)'
          }
        }}
      >
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => (
            <ListItem 
              key={item.path}
              component={Link}
              to={item.path}
              onClick={toggleMobileMenu}
              sx={{
                color: isActive(item.path) ? 'var(--primary)' : '#ffffff',
                bgcolor: isActive(item.path) ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                transition: 'all 0.3s ease',
                borderRadius: 1,
                my: 1,
                mx: 1.5,
                width: 'auto',
                '&:hover': {
                  bgcolor: 'rgba(56, 189, 248, 0.2)',
                  transform: 'translateX(8px)'
                }
              }}
            >
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: isActive(item.path) ? 700 : 600,
                    fontSize: '1.2rem'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Header; 